import z from 'zod';

import {
  CategorySchemaV3,
  HostedBySchemaV3,
  ListingsV3SortBy,
  MediasSchemaV3,
  PromotedListingSchemaV3,
  ResourceGroupBaseSchemaV3,
  ResourceGroupWithExperiencePaymentMethodsSchemaV3,
} from './common';
import { GetListingsV3FeaturedPublicResponseSchema } from './public/listings-v3';
import { IntegrationMetaSchema, PaymentMethods, ShouldValidateComplimentaryBookingSchema } from '../../..';
import { ExperienceVersion } from '../../../experience/const';
import { buildQueryString } from '../../../utils/querystring';
import { ProductLine } from '../../brand/brand';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema, sortOptions, stringBooleanSchema } from '../../common';
import { ExperienceType } from '../../experience/experiences';
import { ResourceGroupCollectionEventType } from '../../resource/common';
import { ScheduleType } from '../../resource/schedule-time-frame';
import { ListingKind, ListingStatus } from '../listing';

export enum ListingsV3Status {
  ALL = 'all',
  APPROVED = 'approved',
  ARCHIVED = 'archived',
  DECLINED = 'declined',
  DRAFT = 'draft',
  PENDING = 'pending',
  PUBLISHED = 'published',
  REQUESTED = 'requested',
  UNPUBLISHED = 'unpublished',
}

export enum PartnerExchangeFilter {
  ALL = 'all',
  PROMOTING = 'promoting',
  PROVIDING = 'providing',
}

export const ListingsV3SummarySortByOptions = [
  ListingsV3SortBy.CREATED_AT,
  ListingsV3SortBy.STATUS,
  ListingsV3SortBy.TITLE,
] as const satisfies ReadonlyArray<ListingsV3SortBy>;

export const GetListingsSummaryV3QuerySchema = paginateQuerySchema({}).extend({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
  hydrateResourceGroups: z.boolean().default(false),
  sortBy: z.enum(ListingsV3SummarySortByOptions).optional(),
  sortDir: z.enum(sortOptions).optional(),
});
export const GetListingsSummaryV3ResponseSchema = paginateResponseSchema(
  z.object({
    eventType: z.nativeEnum(ResourceGroupCollectionEventType).optional().nullable(),
    scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
    experienceId: z.string().uuid().optional().nullable(),
    id: z.string().uuid(),
    kind: z.nativeEnum(ListingKind),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    resourceGroups: z.array(ResourceGroupBaseSchemaV3),
    title: z.string().optional().nullable(),
  }),
);

export type GetListingsSummaryV3Response = z.infer<typeof GetListingsSummaryV3ResponseSchema>;

export const GetListingsSummaryV3Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof GetListingsSummaryV3QuerySchema>; brandId: string }) =>
    `v3/brands/${brandId}/dashboard/listings/summary?${buildQueryString(query)}`,
  responseSchema: GetListingsSummaryV3ResponseSchema,
  querySchema: GetListingsSummaryV3QuerySchema,
  requestSchema: z.void(),
});

export const GetListingsSummaryV3ForHostEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    query,
    brandId,
    hostId,
  }: {
    query: z.input<typeof GetListingsSummaryV3QuerySchema>;
    brandId: string;
    hostId: string;
  }) => `v3/brands/${brandId}/dashboard/hosts/${hostId}/listings/summary?${buildQueryString(query)}`,
  responseSchema: GetListingsSummaryV3ResponseSchema,
  querySchema: GetListingsSummaryV3QuerySchema,
  requestSchema: z.void(),
});

export const ListingsV3CarouselSortByOptions = [
  ListingsV3SortBy.CREATED_AT,
  ListingsV3SortBy.STATUS,
  ListingsV3SortBy.TITLE,
] as const satisfies ReadonlyArray<ListingsV3SortBy>;

export const GetListingsForCarouselV3QuerySchema = paginateQuerySchema({}).extend({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
  sortBy: z.enum(ListingsV3CarouselSortByOptions).optional(),
  sortDir: z.enum(sortOptions).optional(),
});
export const GetListingsForCarouselV3ResponseSchema = paginateResponseSchema(
  z.object({
    eventType: z.nativeEnum(ResourceGroupCollectionEventType).optional().nullable(),
    scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
    experienceId: z.string().uuid().optional().nullable(),
    hasAvailability: z.boolean(),
    hostedBy: HostedBySchemaV3.optional().nullable(),
    id: z.string().uuid(),
    isReservable: z.boolean(),
    isUnlisted: z.boolean(),
    kind: z.nativeEnum(ListingKind),
    medias: z.array(MediasSchemaV3),
    productLine: z.nativeEnum(ProductLine),
    promotedListing: PromotedListingSchemaV3.optional().nullable(),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    status: z.nativeEnum(ListingStatus).optional().nullable(),
    title: z.string().optional().nullable(),
  }),
);

export type GetListingsForCarouselV3Response = z.infer<typeof GetListingsForCarouselV3ResponseSchema>;

export const GetListingsForCarouselV3Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof GetListingsForCarouselV3QuerySchema>; brandId: string }) =>
    `v3/brands/${brandId}/dashboard/listings/carousel?${buildQueryString(query)}`,
  responseSchema: GetListingsForCarouselV3ResponseSchema,
  querySchema: GetListingsForCarouselV3QuerySchema,
  requestSchema: z.void(),
});

export const GetListingsForCarouselV3ForHostEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    query,
    brandId,
    hostId,
  }: {
    query: z.input<typeof GetListingsForCarouselV3QuerySchema>;
    brandId: string;
    hostId: string;
  }) => `v3/brands/${brandId}/dashboard/hosts/${hostId}/listings/carousel?${buildQueryString(query)}`,
  responseSchema: GetListingsForCarouselV3ResponseSchema,
  querySchema: GetListingsForCarouselV3QuerySchema,
  requestSchema: z.void(),
});

export const ListingsV3SortByOptions = [
  ListingsV3SortBy.CATEGORY,
  ListingsV3SortBy.CREATED_AT,
  ListingsV3SortBy.STATUS,
  ListingsV3SortBy.TITLE,
] as const satisfies ReadonlyArray<ListingsV3SortBy>;

export const GetListingsV3QuerySchema = paginateQuerySchema({}).extend({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
  status: z.nativeEnum(ListingsV3Status).optional().nullable(),
  sortBy: z.enum(ListingsV3SortByOptions).optional(),
  sortDir: z.enum(sortOptions).optional(),
  q: z.string().optional(),
  hostId: z.string().uuid().optional(),
  partnerExchange: z.nativeEnum(PartnerExchangeFilter).optional(),
  kind: z.array(z.nativeEnum(ListingKind)).optional(),
  categoryId: z.string().uuid().optional(),
  productLine: z.array(z.nativeEnum(ProductLine)).optional(),
  isBookable: stringBooleanSchema.optional(),
  paymentMethod: z.string().optional(),
  isExclusive: stringBooleanSchema.optional(),
  locationName: z.union([z.string(), z.array(z.string())]).optional(),
  resourcePoolId: z.union([z.string().uuid(), z.array(z.string().uuid())]).optional(),
  priceMin: z.coerce.number().optional(),
  priceMax: z.coerce.number().optional(),
  excludePromoted: stringBooleanSchema.optional(),
  isUnlisted: stringBooleanSchema.optional(),
});
export const GetListingsV3ResponseSchema = paginateResponseSchema(
  z.object({
    category: CategorySchemaV3.optional().nullable(),
    eventType: z.nativeEnum(ResourceGroupCollectionEventType).optional().nullable(),
    scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
    experienceId: z.string().uuid().optional().nullable(),
    hasAvailability: z.boolean(),
    hostedBy: HostedBySchemaV3.optional().nullable(),
    id: z.string().uuid(),
    integrationMeta: z.array(IntegrationMetaSchema).nullable().optional(),
    isReservable: z.boolean(),
    isUnlisted: z.boolean(),
    kind: z.nativeEnum(ListingKind),
    medias: z.array(MediasSchemaV3),
    paymentMethods: z.array(z.nativeEnum(PaymentMethods)).optional().nullable(),
    promotedListing: PromotedListingSchemaV3.optional().nullable(),
    productLine: z.nativeEnum(ProductLine),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    resourceGroups: z.array(ResourceGroupWithExperiencePaymentMethodsSchemaV3),
    shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional().nullable(),
    status: z.nativeEnum(ListingStatus).optional().nullable(),
    title: z.string().optional().nullable(),
    type: z.nativeEnum(ExperienceType).optional().nullable(),
  }),
);

export type GetListingsV3Response = z.infer<typeof GetListingsV3ResponseSchema>;

export const GetListingsV3Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof GetListingsV3QuerySchema>; brandId: string }) =>
    `v3/brands/${brandId}/dashboard/listings?${buildQueryString(query)}`,
  responseSchema: GetListingsV3ResponseSchema,
  querySchema: GetListingsV3QuerySchema,
  requestSchema: z.void(),
});

export const GetListingsV3NeedsAvailabilityQuerySchema = paginateQuerySchema({
  sortBy: ListingsV3SortByOptions,
});

export const GetListingsV3NeedsAvailabilityEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof GetListingsV3NeedsAvailabilityQuerySchema>; brandId: string }) =>
    `v3/brands/${brandId}/dashboard/listings/needs-availability?${buildQueryString(query)}`,
  responseSchema: GetListingsV3ResponseSchema,
  querySchema: GetListingsV3NeedsAvailabilityQuerySchema,
  requestSchema: z.void(),
});

export const GetListingsV3ForHostQuerySchema = paginateQuerySchema({}).extend({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
  status: z.nativeEnum(ListingsV3Status).optional().nullable(),
  sortBy: z.enum(ListingsV3SortByOptions).optional(),
  sortDir: z.enum(sortOptions).optional(),
});

export const GetListingsV3ForHostEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    query,
    brandId,
    hostId,
  }: {
    query: z.input<typeof GetListingsV3ForHostQuerySchema>;
    brandId: string;
    hostId: string;
  }) => `v3/brands/${brandId}/dashboard/hosts/${hostId}/listings?${buildQueryString(query)}`,
  responseSchema: GetListingsV3ResponseSchema,
  querySchema: GetListingsV3ForHostQuerySchema,
  requestSchema: z.void(),
});

export const GetListingsV3OrderedQuerySchema = z.object({
  limit: z.coerce.number().optional(),
});

export const GetListingsV3OrderedResponseSchema = z.array(
  z.object({
    id: z.string().uuid(),
    title: z.string().optional().nullable(),
    kind: z.nativeEnum(ListingKind),
    experienceId: z.string().uuid().optional().nullable(),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    isUnlisted: z.boolean(),
  }),
);

export type GetListingsV3OrderedResponse = z.infer<typeof GetListingsV3OrderedResponseSchema>;

export const GetListingsV3OrderedDashboardEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetListingsV3OrderedQuerySchema> }) =>
    `v3/brands/${brandId}/dashboard/listings/ordered?${buildQueryString(query)}`,
  responseSchema: GetListingsV3OrderedResponseSchema,
  querySchema: GetListingsV3OrderedQuerySchema,
  requestSchema: z.void(),
});

const StatusCountsSchema = z.object({
  archived: z.number(),
  draft: z.number(),
  pending: z.number(),
  published: z.number(),
  unpublished: z.number(),
  needsAvailability: z.number(),
});

export type StatusCounts = z.infer<typeof StatusCountsSchema>;

export const GetListingsV3OverviewResponseSchema = z.object({
  brandOwned: StatusCountsSchema,
  promoted: StatusCountsSchema,
});

export const GetListingsV3OverviewEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v3/brands/${brandId}/dashboard/listings/overview`,
  responseSchema: GetListingsV3OverviewResponseSchema,
  requestSchema: z.void(),
});

export const GetListingsV3OverviewForHostResponseSchema = z.object({
  brandOwned: StatusCountsSchema,
});

export const GetListingsV3OverviewForHostEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, hostId }: { brandId: string; hostId: string }) =>
    `v3/brands/${brandId}/dashboard/hosts/${hostId}/listings/overview`,
  responseSchema: GetListingsV3OverviewForHostResponseSchema,
  requestSchema: z.void(),
});

export const GetListingsV3FeaturedDashboardEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v3/brands/${brandId}/dashboard/listings/featured`,
  responseSchema: GetListingsV3FeaturedPublicResponseSchema,
  requestSchema: z.void(),
});
